/**
 * Контракт извлечения статьи. docs/phase-2/01-monitoring.md §4.4, §6.2.
 *
 * Дозагрузка статьи — вторая стадия и отдельный контракт, а не режим SourceParser: у неё
 * другой вход (одна страница, не лента), другой критерий успеха (длина текста) и другая
 * заменяемая реализация (ReadabilityArticleExtractor в packages/source-parsers).
 *
 * too_short отделён от stub намеренно: короткая страница — это чаще всего анонс или пейволл,
 * и реакция на неё другая (материал идёт дальше с тем текстом, что есть, если он выше
 * абсолютного минимума; источнику это не засчитывается в отказ). stub — это отказ источника.
 */

import * as z from 'zod';
import type { FetchedResponse } from './fetcher.ts';
import { STUB_SIGNALS, type StubSignal } from './parser.ts';

/**
 * Порог длины по умолчанию, КОДПОИНТЫ (§6.2.3). Поле реестра источника
 * (min_article_chars); значение калибруется по корпусу подфазы CLI.
 */
export const ARTICLE_MIN_CHARS_DEFAULT = 600;

export interface ArticleOutcome_Ok {
  readonly kind: 'article';
  readonly text: string; // plain text, абзацы разделены '\n\n'
  readonly title: string | null;
  readonly publishedAtMs: number | null; // из meta, если нашлась
  readonly publishedAtRaw: string | null;
  readonly lang: string | null;
  readonly charCount: number; // в кодпоинтах
  readonly canonicalUrl: string | null; // <link rel=canonical>
}

export type ArticleOutcome =
  | ArticleOutcome_Ok
  | { readonly kind: 'too_short'; readonly charCount: number; readonly threshold: number }
  | { readonly kind: 'stub'; readonly signal: StubSignal; readonly detail: string }
  | { readonly kind: 'malformed'; readonly detail: string };

export interface ArticleExtractor {
  /** Синхронный: вход уже скачан, разбор не ходит в сеть. */
  extract(res: FetchedResponse, url: string, minChars: number): ArticleOutcome;
}

/* ── zod-схемы: та же форма для валидации фикстур корпуса и сериализованных исходов ── */

export const ArticleOutcomeSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('article'),
    text: z.string().min(1),
    title: z.string().nullable(),
    publishedAtMs: z.int().nullable(),
    publishedAtRaw: z.string().nullable(),
    lang: z.string().nullable(),
    charCount: z.int().nonnegative(),
    canonicalUrl: z.string().nullable(),
  }),
  z.strictObject({
    kind: z.literal('too_short'),
    charCount: z.int().nonnegative(),
    threshold: z.int().positive(),
  }),
  z.strictObject({ kind: z.literal('stub'), signal: z.enum(STUB_SIGNALS), detail: z.string() }),
  z.strictObject({ kind: z.literal('malformed'), detail: z.string() }),
]);
